import {
    h, Component, toChildArray, Fragment, ComponentChildren
} from "preact";

import Portal from "../Portal";

import classname from "../../foundation/utils/classname";
import noderef from "../../foundation/utils/noderef";
import { stopPropagation } from "../../foundation/utils/legacy";
import NodeRefs from "../../foundation/utils/NodeRefs";
import { Keyboard } from "../../foundation/enums";

import "./style.css";

interface DefaultProps {
    offsetX: number;
    topOffset: number;
    bottomOffset: number;
    left: number;
    top: number;
    anchorTop: number;
}

interface Props extends Partial<DefaultProps>{
    open: boolean;
    targetNode?: HTMLElement | null;
    faceLeft?: boolean;
    faceTop?: boolean;
    centerX?: boolean;
    centerY?: boolean;
    stickToEdge?: boolean;
    showAnchor?: boolean;
    openToLeft?: boolean;
    openToBottom?: boolean;
    children?: ComponentChildren;
    portalInto?: HTMLElement;
    contentClass?: string;
    class?: string;
    content: ComponentChildren;
    overlay?: boolean;
    onMouseEnter?: (ev: MouseEvent) => void;
    onMouseLeave?: (ev: MouseEvent) => void;
    onClose?: () => void;
    onOpen?: () => void;
}

type PropsWithDefaults = Props & DefaultProps;

class Popover extends Component<Props> {
    static override defaultProps: DefaultProps = {
        offsetX: 0,
        topOffset: 0,
        bottomOffset: 12,
        left: 0,
        top: 0,
        anchorTop: 0
    };

    nodeRefs: NodeRefs = {};
    // eslint-disable-next-line complexity
    positionPopover = () => {
        const {
            open,
            targetNode,
            faceLeft,
            faceTop,
            centerX,
            centerY,
            stickToEdge,
            showAnchor,
            openToLeft,
            openToBottom,
            offsetX,
            topOffset,
            bottomOffset,
            left,
            top,
            anchorTop
        } = this.props as PropsWithDefaults;

        const baseComponent = targetNode ?? this.nodeRefs["container"];
        const { popover, anchor } = this.nodeRefs;

        const originParentWidth = window.innerWidth;
        const originParentHeight = window.innerHeight;

        if (!open || !popover || !baseComponent) {
            return;
        }

        const containerRect = baseComponent.getBoundingClientRect();
        const popoverRect = popover.getBoundingClientRect();

        const displacementX = left;
        const displacementY = top;

        let deltaX = faceLeft ? containerRect.left : containerRect.right;
        if (centerX) {
            deltaX += (containerRect.width - popoverRect.width) / 2;
        } else {
            deltaX += displacementX;
        }

        let deltaY = faceTop || showAnchor ? containerRect.top : containerRect.bottom;
        if (centerY) {
            deltaY += (containerRect.height - popoverRect.height) / 2;
        } else {
            deltaY += displacementY;
        }

        let facingLeft = false;
        const overflowsX = deltaX + popoverRect.width + offsetX > originParentWidth;

        const newDeltaX = containerRect.left - popoverRect.width - left;
        if (overflowsX || (openToLeft && newDeltaX > offsetX)) {
            // show on left of the baseComponent
            deltaX = newDeltaX;
            facingLeft = true;
        }

        let facingTop = false;
        const overflowsY = deltaY + popoverRect.height + topOffset > originParentHeight;

        if (stickToEdge && deltaY + topOffset < 0) {
            deltaY = topOffset || 64;
        } else if (overflowsY) {
            // show on top of the baseComponent
            deltaY = stickToEdge
                ? originParentHeight - popoverRect.height - bottomOffset : Math.max(deltaY - popoverRect.height, 0);
        }

        if (topOffset && deltaY < topOffset) {
            deltaY = topOffset;
        }

        if (openToBottom) {
            deltaX = containerRect.left + displacementX;
            if (overflowsX) {
                // slide left to fit into the screen
                deltaX = originParentWidth - popoverRect.width - offsetX;
            }
            deltaY = containerRect.bottom + bottomOffset;
            if (overflowsY) {
                // show on top of baseComponent
                deltaY = containerRect.top - popoverRect.height - bottomOffset;
                facingTop = true;
            }
        }

        popover.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0)`;
        if (showAnchor) {
            // calculate anchor to point to the baseComponent
            if (openToBottom) {
                // place the anchor on top of the popover
                const computedAnchorLeft = containerRect.left - deltaX +
                    (containerRect.width - anchor!.offsetWidth) / 2;
                anchor!.style.left = `${Math.min(Math.max(computedAnchorLeft, 28), popoverRect.width - 28)}px`;
                // place anchor on bottom instead
                anchor!.classList.toggle("faceTop", facingTop);
            } else {
                // place the anchor on left of the popover
                const computedAnchorTop = containerRect.top - deltaY +
                                        (containerRect.height - anchor!.offsetHeight) / 2 + anchorTop;
                anchor!.style.top = `${Math.min(Math.max(computedAnchorTop, 28), popoverRect.height - 28)}px`;
                // place anchor on right instead
                anchor!.classList.toggle("faceLeft", facingLeft);
            }
        }
    };

    registerListeners = () => {
        window.addEventListener("resize", this.handleResize);
        document.addEventListener("click", this.handleDocumentClick, true);
        document.addEventListener("keydown", this.handleKeyDown);
    };

    removeListeners = () => {
        window.removeEventListener("resize", this.handleResize);
        document.removeEventListener("click", this.handleDocumentClick, true);
        document.removeEventListener("keydown", this.handleKeyDown);
    };

    handleToggle = (ev: MouseEvent) => {
        const { open, onOpen, onClose } = this.props;

        ev.stopPropagation();

        if (open && onClose) {
            onClose();
        } else if (!open && onOpen) {
            onOpen();
        }
    };

    handleClose = () => {
        const { onClose } = this.props;
        if (onClose) {
            onClose();
        }
    };

    handleDocumentClick = (ev: MouseEvent) => {
        if (ev.button !== Zeplin.MOUSE_BUTTON.LEFT) {
            return;
        }

        const { container, popover } = this.nodeRefs;

        // If popover button is clicked, prevent opening again
        const isPopoverClicked = container!.contains(ev.target as Node);
        if (isPopoverClicked) {
            return;
        }

        // If self click, do nothing
        const isPopoverContentClicked = popover?.contains(ev.target as Node);
        if (isPopoverContentClicked) {
            return;
        }

        this.handleClose();
    };

    handleKeyDown = (ev: KeyboardEvent) => {
        if (ev.key === Keyboard.Key.Escape) {
            this.handleClose();
        }
    };

    handleResize = () => {
        requestAnimationFrame(this.positionPopover);
    };

    handleMouseEnter = (event: MouseEvent) => {
        const { onMouseEnter } = this.props;

        if (onMouseEnter) {
            onMouseEnter(event);
        }
    };

    handleMouseLeave = (event: MouseEvent) => {
        const { onMouseLeave } = this.props;

        if (onMouseLeave) {
            onMouseLeave(event);
        }
    };

    override componentDidMount() {
        const { targetNode, open } = this.props;

        if (targetNode && open) {
            this.registerListeners();
        }
    }

    override componentDidUpdate(prevProps: Props) {
        const { open, content } = this.props;

        if (!prevProps.open && open) {
            this.registerListeners();
        } else if (prevProps.open && !open) {
            this.removeListeners();
        }
        if (prevProps.content !== content) {
            this.positionPopover();
        }
    }

    override componentWillUnmount() {
        this.removeListeners();
    }

    renderAnchor() {
        const { faceLeft, openToBottom } = this.props;
        const anchorClassnames = classname("popoverAnchor", { faceLeft, openToBottom });

        return <div ref={noderef(this, "anchor")} class={anchorClassnames}></div>;
    }

    renderPopoverContent() {
        const { content, showAnchor, contentClass, overlay } = this.props;
        const classnames = classname("popoverContent", contentClass);

        const popoverContent = (
            <div
                class={classnames}
                ref={noderef(this, "popover")}
                onClick={stopPropagation}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}>
                {showAnchor && this.renderAnchor()}
                {toChildArray(content)}
            </div>
        );

        if (overlay) {
            return <div class="popoverContentOverlay">{popoverContent}</div>;
        }

        return popoverContent;
    }

    render() {
        const { open, children, portalInto, class: cssClass } = this.props;
        const classes = classname(cssClass, "popoverContainer");

        return (
            <div
                ref={noderef(this, "container")}
                class={classes}
                onClick={this.handleToggle}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}>
                <>
                    {children}
                    {open && (
                        <Portal didRender={this.positionPopover} into={portalInto}>
                            {this.renderPopoverContent()}
                        </Portal>
                    )}
                </>
            </div>
        );
    }
}

export default Popover;
